import React from 'react';
import { ReleaseWaveform } from '@components/Waveform/Release/ReleaseWaveform';
import { WebWorkerImage } from '@components/basics/Images/WebWorkerImage';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, gql, LandingHeroSlideFragmentDoc } from '@graphql/reactQuery';
import { useIsSsr } from '@hooks/useIsSsr';
import { PlayAndReleaseInfo } from './PlayAndReleaseInfo';
import { TopCollectorsDesktop } from './TopCollectorsDesktop';
import { TopCollectorsMobile } from './TopCollectorsMobile';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment LandingHeroSlide on Release {
    id
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    animatedCoverImage {
      id
      url
    }
    ...LandingHeroPlayRelease
    ...LandingHeroTopCollectors
    ...WaveformInfo
  }
`);

const MASK_IMAGE = 'linear-gradient(90deg, transparent, black 2%, black 98%, transparent)';
const MAX_IMAGE_SIZE = 600;

export const LandingHeroSlide = ({
  index,
  release,
  setIsHovering,
  getNextTrackIds,
}: {
  index: number;
  release: FragmentType<LandingHeroSlideFragmentDoc>;
  setIsHovering: (isHovering: boolean) => void;
  getNextTrackIds: () => string[];
}) => {
  const releaseFrag = getFragment(LandingHeroSlideFragmentDoc, release);
  const { animatedCoverImage, coverImage, staticCoverImage } = releaseFrag;
  const { tablet } = MediaQuery.useContainer();
  const isSsr = useIsSsr();

  const IS_PRIORITY_IMAGE = index === 0 ? true : false;

  return (
    <div
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className="relative flex h-[444px] items-center justify-between overflow-hidden rounded-xl bg-base900 md:h-[302px] md:px-10 lt:h-[426px]"
    >
      <div
        style={{
          // Avoids glitchty borders of image when slide is moving on tablet and above
          maskImage: tablet ? MASK_IMAGE : undefined,
          WebkitMaskImage: tablet ? MASK_IMAGE : undefined,
        }}
        className="absolute right-0 top-0 h-[356px] w-full md:right-36 md:top-[-41px] md:h-[384px] md:w-[384px] lt:top-[-87px] lt:h-[600px] lt:w-[600px]"
      >
        {!!animatedCoverImage && !isSsr ? (
          <WebWorkerImage
            className="h-full w-full object-cover object-center"
            src={animatedCoverImage.url}
            width={MAX_IMAGE_SIZE}
            height={MAX_IMAGE_SIZE}
            alt="cover image"
          />
        ) : (
          <NextFutureImage
            className="h-full w-full object-cover object-center"
            src={staticCoverImage?.url ?? coverImage.url}
            width={MAX_IMAGE_SIZE}
            height={MAX_IMAGE_SIZE}
            alt="cover image"
            priority={IS_PRIORITY_IMAGE}
          />
        )}
        <div className="absolute bottom-0 hidden h-full w-full bg-[radial-gradient(50%_50.1%_at_50%_49.9%,_rgba(15,15,15,0)_0%,_#0F0F0F_100%)] md:block" />
        <div className="absolute -bottom-2 h-[110px] w-full bg-[linear-gradient(0deg,#0E1213_44.55%,rgba(14,18,19,0)_100%)] md:hidden" />
      </div>
      <div className="z-base mb-[94px] mt-auto flex w-full items-center justify-between px-4 text-white md:my-0 md:block md:max-w-[50%] md:px-0">
        <PlayAndReleaseInfo release={releaseFrag} getNextTrackIds={getNextTrackIds} />
      </div>
      <div className="absolute bottom-16 left-0 right-0 md:bottom-0">
        <ReleaseWaveform
          release={releaseFrag}
          isTrackDisabled={false}
          showComments={false}
          showBottomHalf={false}
          enableTouchHandler={false}
        />
      </div>
      <div className="z-base hidden text-white md:block">
        <TopCollectorsDesktop release={releaseFrag} />
      </div>
      <div className="absolute bottom-0 z-base flex h-16 w-full items-center px-4 text-white md:hidden">
        <TopCollectorsMobile release={releaseFrag} />
      </div>
    </div>
  );
};
